import { Spin } from "antd";
import classNames from "classnames";
import { get, isEmpty, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { fieldPhrase } from "../../data-fields";
import { ZONE_TYPES } from "../../field-status";
import { PROPOSAL_STATUSES } from "./draft-content";
import {
  pendingProposalOf,
  proposalValueText,
  ZONE_STATUSES,
  zoneName,
} from "./zones";

const MIN_PLACES = 2;
const SEARCHING_KEY = "ZONES.SEARCHING";

const BUTTONS = {
  PRIMARY:
    "dynamic-documents-editor__btn dynamic-documents-editor__btn--sm dynamic-documents-editor__btn--primary",
  SECONDARY: "dynamic-documents-editor__btn dynamic-documents-editor__btn--sm",
  LINK: "dynamic-documents-editor__link dynamic-documents-editor__row-link",
};

const singleClick = (action) => (event) => event.detail > 1 || action();

const pageList = (pages, language) => {
  const numbers = pages.map((page) => String(page + 1));
  try {
    return new Intl.ListFormat(language, { type: "conjunction" }).format(
      numbers,
    );
  } catch (_error) {
    return numbers.join(", ");
  }
};

const ICONS = {
  [ZONE_STATUSES.READY]: (
    <>
      <circle cx="12" cy="12" r="10" fill="#1f7a45" />
      <path
        d="M7.5 12.5l3 3 6-6.5"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  [ZONE_STATUSES.CHECK]: (
    <>
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="#fff4dc"
        stroke="#b7790f"
        strokeWidth="2"
      />
      <circle cx="12" cy="12" r="4" fill="#b7790f" />
    </>
  ),
  [ZONE_STATUSES.TODO]: (
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="#ffffff"
      stroke="#c0392b"
      strokeWidth="2.6"
    />
  ),
  [ZONE_STATUSES.EMPTY]: (
    <>
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="#fff0e0"
        stroke="#c4600f"
        strokeWidth="2"
      />
      <path
        d="M12 7v6M12 16.5v.5"
        stroke="#c4600f"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </>
  ),
  [ZONE_STATUSES.SIGN]: (
    <g
      fill="none"
      stroke="#5d6780"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
    </g>
  ),
};

export const StatusIcon = ({ status }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const label = t(`ZONES.STATUSES.${status}`);
  return (
    <span className="dynamic-documents-editor__status" title={label}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        role="img"
        aria-label={label}
      >
        {ICONS[status]}
      </svg>
    </span>
  );
};

const Actions = ({
  zone,
  primary,
  secondary,
  onPrimary,
  onSecondary,
  onLeaveEmpty,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  return (
    <span className="dynamic-documents-editor__row-actions">
      <button
        type="button"
        className={BUTTONS.PRIMARY}
        data-row-primary
        onClick={singleClick(() => onPrimary(zone))}
      >
        {primary}
      </button>
      {!isNil(secondary) && (
        <button
          type="button"
          className={BUTTONS.SECONDARY}
          onClick={singleClick(() => onSecondary(zone))}
        >
          {secondary}
        </button>
      )}
      {zone.type === ZONE_TYPES.ANCHOR && (
        <button
          type="button"
          className={BUTTONS.LINK}
          onClick={singleClick(() => onLeaveEmpty(zone))}
        >
          {t("ZONES.LEAVE_EMPTY")}
        </button>
      )}
    </span>
  );
};

const questionKey = ({ zone, proposals, searching }) => {
  const table = zone.type === ZONE_TYPES.CHILD_LOOP ? "_TABLE" : "";
  const asked = get(proposals, zone.id);
  if (searching && isNil(asked)) return SEARCHING_KEY;
  if (get(asked, "status") === PROPOSAL_STATUSES.NONE)
    return `ZONES.NOTHING_FOUND${table}`;
  return `ZONES.NOT_ASKED${table}`;
};

export const ProposalLine = ({
  zone,
  proposals,
  payload,
  samplePrefix,
  searching,
  onFill,
  onOpen,
  onLeaveEmpty,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const table = zone.type === ZONE_TYPES.CHILD_LOOP;
  const proposal = pendingProposalOf(zone, proposals);

  if (!isNil(proposal)) {
    const value =
      proposalValueText({ zone, proposal, payload, t }) ??
      t("ZONES.VALUE_EMPTY");
    return (
      <div className="dynamic-documents-editor__row-line">
        <p
          className="dynamic-documents-editor__row-what"
          title={proposal.reason ?? undefined}
        >
          {t(table ? "ZONES.PROPOSAL_TABLE" : "ZONES.PROPOSAL_WRITE")}{" "}
          <strong>{fieldPhrase(proposal.field)}</strong>
        </p>
        <p className="dynamic-documents-editor__row-sample" title={value}>
          {samplePrefix}{" "}
          <span className="dynamic-documents-editor__row-sample-value">
            {value}
          </span>
        </p>
        <Actions
          zone={zone}
          primary={t("ZONES.FILL")}
          secondary={t("ZONES.CHOOSE_OTHER")}
          onPrimary={onFill}
          onSecondary={onOpen}
          onLeaveEmpty={onLeaveEmpty}
        />
      </div>
    );
  }

  const key = questionKey({ zone, proposals, searching });
  return (
    <div className="dynamic-documents-editor__row-line">
      <p className="dynamic-documents-editor__row-what">
        {key === SEARCHING_KEY && <Spin size="small" />}
        {t(key)}
      </p>
      <Actions
        zone={zone}
        primary={t(table ? "ZONES.CHOOSE_LIST" : "ZONES.CHOOSE")}
        onPrimary={onOpen}
        onLeaveEmpty={onLeaveEmpty}
      />
    </div>
  );
};

const RowChips = ({ zone, place }) => {
  const { t, i18n } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const table = zone.type === ZONE_TYPES.CHILD_LOOP;
  const places = get(place, "places") ?? 0;
  const chips = [
    ...(table ? [{ key: "table", text: t("ZONES.CHIP_TABLE") }] : []),
    ...(zone.type === ZONE_TYPES.CHECKBOX_GROUP
      ? [{ key: "checkboxes", text: t("ZONES.CHIP_CHECKBOXES") }]
      : []),
    ...(!table && places >= MIN_PLACES
      ? [
          {
            key: "places",
            text: t("ZONES.PLACES", { count: places }),
            title: t("ZONES.PLACES_PAGES", {
              count: place.pages.length,
              pages: pageList(place.pages, i18n.language),
            }),
          },
        ]
      : []),
  ];
  if (isEmpty(chips)) return null;
  return (
    <span className="dynamic-documents-editor__chips">
      {chips.map(({ key, text, title }) => (
        <span
          key={key}
          className="dynamic-documents-editor__chip"
          title={title}
        >
          {text}
        </span>
      ))}
    </span>
  );
};

export const ZoneRow = ({
  zone,
  title,
  status,
  place,
  showValue = false,
  value,
  valueTone,
  valueTitle,
  open,
  pointed = false,
  leaving = false,
  line,
  panel,
  onToggle,
  onHover,
  onFocus,
  onBlur,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const crossesRow = (event) =>
    !event.currentTarget.contains(event.relatedTarget);
  return (
    <li
      className={classNames("dynamic-documents-editor__row", {
        "dynamic-documents-editor__row--open": open,
        "dynamic-documents-editor__row--pointed": pointed,
        "dynamic-documents-editor__row--leaving": leaving,
      })}
      data-zone-row={zone.id}
      data-leaving={leaving || undefined}
      aria-hidden={leaving || undefined}
      onMouseEnter={() => onHover?.(zone)}
      onMouseLeave={() => onHover?.(null)}
      onFocus={(event) => {
        if (crossesRow(event)) onFocus?.(zone);
      }}
      onBlur={(event) => {
        if (crossesRow(event)) onBlur?.(zone);
      }}
    >
      <div className="dynamic-documents-editor__row-head">
        <StatusIcon status={status} />
        <button
          type="button"
          className="dynamic-documents-editor__row-label"
          data-row-label
          title={t("ZONES.RAW_NAME", { name: zoneName(zone) })}
          aria-expanded={open}
          onClick={() => onToggle(zone)}
        >
          {title}
        </button>
        <RowChips zone={zone} place={place} />
        {showValue && (
          <span
            className={classNames("dynamic-documents-editor__row-value", {
              [`dynamic-documents-editor__row-value--${valueTone}`]:
                !isNil(valueTone),
            })}
            title={valueTitle ?? value ?? undefined}
          >
            {value ?? "—"}
          </span>
        )}
      </div>
      {!open && line}
      {open && panel}
    </li>
  );
};
